import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import {
  defaultSectionVisibility,
  defaultSectionOrder,
  defaultLogoConfig,
  type LogoConfig,
} from "@/data/siteContent";
import { stagingStoreSet, stagingStoreNotify, discardAllStagedChanges } from "@/hooks/useAdminStorage";

const HARD_CODED_PASSWORD = "makersalley2024";
const SESSION_KEY = "ma_admin_session";
const SECTION_VISIBILITY_KEY = "ma_section_visibility";
const SECTION_ORDER_KEY = "ma_section_order";
const SECTION_DATA_MAP_KEY = "ma_section_data_map";
const LOGO_FONT_KEY = "ma_logo_font";
const LOGO_CONFIG_KEY = "ma_logo_config";
const MOBILE_PREVIEW_KEY = "ma_admin_mobile_preview";

/** Read the current admin password from localStorage (if changed) or fall back to hardcoded */
function getCurrentPassword(): string {
  try {
    const raw = localStorage.getItem("ma_admin_password");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed === "string" && parsed.length > 0) return parsed;
    }
  } catch { /* ignore */ }
  return HARD_CODED_PASSWORD;
}

export const DEFAULT_SECTION_ORDER = defaultSectionOrder;

// ... existing font options ...

export interface LogoFontOption {
  id: string;
  name: string;
  fontFamily: string;
  googleFont: string;
  preview: string;
  style?: React.CSSProperties;
}

export const LOGO_FONT_OPTIONS: LogoFontOption[] = [
  {
    id: "playfair",
    name: "Playfair Display",
    fontFamily: "'Playfair Display', serif",
    googleFont: "Playfair+Display:wght@700",
    preview: "makers alley co",
    style: { fontStyle: "normal" },
  },
  {
    id: "trap-sans",
    name: "Trap Sans Serif",
    fontFamily: "'Trap', sans-serif",
    googleFont: "",
    preview: "MAKERS ALLEY CO",
    style: { letterSpacing: "0.08em", fontWeight: 800 },
  },
  {
    id: "bebas",
    name: "Bebas Neue",
    fontFamily: "'Bebas Neue', sans-serif",
    googleFont: "Bebas+Neue",
    preview: "MAKERS ALLEY CO",
    style: { letterSpacing: "0.12em" },
  },
  {
    id: "cormorant",
    name: "Cormorant Garamond",
    fontFamily: "'Cormorant Garamond', serif",
    googleFont: "Cormorant+Garamond:wght@700",
    preview: "makers alley co",
    style: { fontStyle: "italic" },
  },
  {
    id: "josefin",
    name: "Josefin Sans",
    fontFamily: "'Josefin Sans', sans-serif",
    googleFont: "Josefin+Sans:wght@700",
    preview: "MAKERS ALLEY CO",
    style: { letterSpacing: "0.18em" },
  },
  {
    id: "dm-serif",
    name: "DM Serif Display",
    fontFamily: "'DM Serif Display', serif",
    googleFont: "DM+Serif+Display",
    preview: "makers alley co",
    style: {},
  },
  {
    id: "space-grotesk",
    name: "Space Grotesk",
    fontFamily: "'Space Grotesk', sans-serif",
    googleFont: "Space+Grotesk:wght@700",
    preview: "makers alley co",
    style: { letterSpacing: "-0.02em" },
  },
  {
    id: "outfit",
    name: "Outfit",
    fontFamily: "'Outfit', sans-serif",
    googleFont: "Outfit:wght@700;800",
    preview: "Makers Alley Co",
    style: { fontWeight: 800 },
  },
  {
    id: "syne",
    name: "Syne",
    fontFamily: "'Syne', sans-serif",
    googleFont: "Syne:wght@700;800",
    preview: "MAKERS ALLEY CO",
    style: { fontWeight: 800, letterSpacing: "0.04em" },
  },
  {
    id: "unbounded",
    name: "Unbounded",
    fontFamily: "'Unbounded', sans-serif",
    googleFont: "Unbounded:wght@700;800",
    preview: "MAKERS ALLEY",
    style: { fontWeight: 800, letterSpacing: "0.02em" },
  },
  {
    id: "italiana",
    name: "Italiana",
    fontFamily: "'Italiana', serif",
    googleFont: "Italiana",
    preview: "Makers Alley Co",
    style: {},
  },
  {
    id: "cinzel",
    name: "Cinzel",
    fontFamily: "'Cinzel', serif",
    googleFont: "Cinzel:wght@700;900",
    preview: "MAKERS ALLEY CO",
    style: { letterSpacing: "0.1em" },
  },
  {
    id: "pinecone",
    name: "Pinecone MVB",
    fontFamily: "pinecone-mvb, serif",
    googleFont: "",
    preview: "Makers Alley Co",
    style: { fontWeight: 700, letterSpacing: "0.02em" },
  },
];

export const DEFAULT_LOGO_CONFIG: LogoConfig = defaultLogoConfig;

// ... existing helpers ...

function loadSectionVisibility(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(SECTION_VISIBILITY_KEY);
    if (raw) return { ...defaultSectionVisibility, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { ...defaultSectionVisibility };
}

function loadSectionOrder(): string[] {
  try {
    const raw = localStorage.getItem(SECTION_ORDER_KEY);
    if (raw) {
      const saved: string[] = JSON.parse(raw);
      const merged = [...saved];
      defaultSectionOrder.forEach((k) => { if (!merged.includes(k)) merged.push(k); });
      return merged;
    }
  } catch { /* ignore */ }
  return [...defaultSectionOrder];
}

function loadSectionDataMap(): Record<string, string> {
  try {
    const raw = localStorage.getItem(SECTION_DATA_MAP_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return {};
}

function loadLogoFont(): string {
  try {
    const raw = localStorage.getItem(LOGO_FONT_KEY);
    if (raw) {
      try { return JSON.parse(raw) as string; } catch { return raw; }
    }
  } catch { /* ignore */ }
  return "playfair";
}

function loadLogoConfig(): LogoConfig {
  try {
    const raw = localStorage.getItem(LOGO_CONFIG_KEY);
    if (raw) return { ...defaultLogoConfig, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { ...defaultLogoConfig };
}

function loadMobilePreview(): boolean {
  try {
    const raw = localStorage.getItem(MOBILE_PREVIEW_KEY);
    if (raw) return raw === "true";
  } catch { /* ignore */ }
  return false;
}

interface AdminContextValue {
  isAdmin: boolean;
  isEditing: boolean;
  login: (password: string) => boolean;
  logout: () => void;
  changePassword: (newPassword: string) => void;
  getCurrentPassword: () => string;
  // Section visibility
  sectionVisibility: Record<string, boolean>;
  isSectionVisible: (key: string) => boolean;
  toggleSection: (key: string) => void;
  // Section order
  sectionOrder: string[];
  setSectionOrder: (order: string[]) => void;
  // Section data key mapping (for duplicated/new sections)
  sectionDataMap: Record<string, string>;
  setSectionDataMap: (map: Record<string, string>) => void;
  // Preview mode
  isPreviewMode: boolean;
  setPreviewMode: (v: boolean) => void;
  // Logo font
  logoFontId: string;
  setLogoFontId: (id: string) => void;
  getLogoFont: () => LogoFontOption;
  // Logo config
  logoConfig: LogoConfig;
  setLogoConfig: (config: LogoConfig) => void;
  // Mobile preview
  isMobilePreview: boolean;
  setIsMobilePreview: (v: boolean) => void;
  // Discard all changes
  discardChanges: () => void;
  // Incremented on each discard
  discardVersion: number;
  // Re-sync from localStorage (called after SiteContentProvider loads)
  reSyncFromStorage: () => void;
}

const AdminContext = createContext<AdminContextValue>({
  isAdmin: false,
  isEditing: false,
  login: () => false,
  logout: () => {},
  changePassword: () => {},
  getCurrentPassword: () => HARD_CODED_PASSWORD,
  sectionVisibility: { ...defaultSectionVisibility },
  isSectionVisible: () => true,
  toggleSection: () => {},
  sectionOrder: [...DEFAULT_SECTION_ORDER],
  setSectionOrder: () => {},
  sectionDataMap: {},
  setSectionDataMap: () => {},
  isPreviewMode: false,
  setPreviewMode: () => {},
  logoFontId: "playfair",
  setLogoFontId: () => {},
  getLogoFont: () => LOGO_FONT_OPTIONS[0],
  logoConfig: { ...DEFAULT_LOGO_CONFIG },
  setLogoConfig: () => {},
  isMobilePreview: false,
  setIsMobilePreview: () => {},
  discardChanges: () => {},
  discardVersion: 0,
  reSyncFromStorage: () => {},
});

export function AdminProvider({ children }: { children: ReactNode }) {
  const [isAdmin, setIsAdmin] = useState<boolean>(() => {
    try { return sessionStorage.getItem(SESSION_KEY) === "true"; } catch { return false; }
  });

  const [sectionVisibility, setSectionVisibility] = useState<Record<string, boolean>>(loadSectionVisibility);
  const [sectionOrder, setSectionOrderState] = useState<string[]>(loadSectionOrder);
  const [sectionDataMap, setSectionDataMapState] = useState<Record<string, string>>(loadSectionDataMap);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [logoFontId, setLogoFontIdState] = useState<string>(loadLogoFont);
  const [logoConfig, setLogoConfigState] = useState<LogoConfig>(loadLogoConfig);
  const [isMobilePreview, setIsMobilePreviewState] = useState<boolean>(loadMobilePreview);
  const [discardVersion, setDiscardVersion] = useState(0);

  const isEditing = isAdmin && !isPreviewMode;

  const login = useCallback((password: string): boolean => {
    if (password === getCurrentPassword()) {
      setIsAdmin(true);
      try { sessionStorage.setItem(SESSION_KEY, "true"); } catch { /* ignore */ }
      return true;
    }
    return false;
  }, []);

  const logout = useCallback(() => {
    setIsAdmin(false);
    setIsPreviewMode(false);
    try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
  }, []);

  const changePassword = useCallback((newPassword: string) => {
    try {
      localStorage.setItem("ma_admin_password", JSON.stringify(newPassword));
      stagingStoreSet("ma_admin_password", newPassword);
      stagingStoreNotify("ma_admin_password");
    } catch { /* ignore */ }
  }, []);

  const getCurrentPasswordCallback = useCallback(() => getCurrentPassword(), []);

  const isSectionVisible = useCallback((key: string): boolean => {
    return sectionVisibility[key] !== false;
  }, [sectionVisibility]);

  const toggleSection = useCallback((key: string) => {
    setSectionVisibility((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      stagingStoreSet(SECTION_VISIBILITY_KEY, next);
      stagingStoreNotify(SECTION_VISIBILITY_KEY);
      return next;
    });
  }, []);

  const setSectionOrder = useCallback((order: string[]) => {
    setSectionOrderState(order);
    stagingStoreSet(SECTION_ORDER_KEY, order);
    stagingStoreNotify(SECTION_ORDER_KEY);
  }, []);

  const setSectionDataMap = useCallback((map: Record<string, string>) => {
    setSectionDataMapState(map);
    stagingStoreSet(SECTION_DATA_MAP_KEY, map);
    stagingStoreNotify(SECTION_DATA_MAP_KEY);
  }, []);

  const discardChanges = useCallback(() => {
    setSectionVisibility(loadSectionVisibility());
    setSectionOrderState(loadSectionOrder());
    setSectionDataMapState(loadSectionDataMap());
    setLogoFontIdState(loadLogoFont());
    setLogoConfigState(loadLogoConfig());
    setIsMobilePreviewState(loadMobilePreview());
    setIsPreviewMode(false);
    discardAllStagedChanges();
    setDiscardVersion((v) => v + 1);
  }, []);

  /**
   * Re-reads localStorage values for section visibility, order, data map,
   * logo config, and font. Called by App.tsx after SiteContentProvider
   * finishes loading so that cloud-synced values override defaults.
   */
  const reSyncFromStorage = useCallback(() => {
    setSectionVisibility(loadSectionVisibility());
    setSectionOrderState(loadSectionOrder());
    setSectionDataMapState(loadSectionDataMap());
    setLogoFontIdState(loadLogoFont());
    setLogoConfigState(loadLogoConfig());
    setIsMobilePreviewState(loadMobilePreview());
  }, []);

  const setPreviewMode = useCallback((v: boolean) => {
    setIsPreviewMode(v);
  }, []);

  const setLogoFontId = useCallback((id: string) => {
    setLogoFontIdState(id);
    stagingStoreSet(LOGO_FONT_KEY, id);
    stagingStoreNotify(LOGO_FONT_KEY);
  }, []);

  const getLogoFont = useCallback((): LogoFontOption => {
    return LOGO_FONT_OPTIONS.find((f) => f.id === logoFontId) ?? LOGO_FONT_OPTIONS[0];
  }, [logoFontId]);

  const setLogoConfig = useCallback((config: LogoConfig) => {
    setLogoConfigState(config);
    stagingStoreSet(LOGO_CONFIG_KEY, config);
    stagingStoreNotify(LOGO_CONFIG_KEY);
  }, []);

  const setIsMobilePreview = useCallback((v: boolean) => {
    setIsMobilePreviewState(v);
    try { localStorage.setItem(MOBILE_PREVIEW_KEY, String(v)); } catch { /* ignore */ }
  }, []);

  return (
    <AdminContext.Provider value={{
      isAdmin, isEditing, login, logout, changePassword, getCurrentPassword: getCurrentPasswordCallback,
      sectionVisibility, isSectionVisible, toggleSection,
      sectionOrder, setSectionOrder,
      sectionDataMap, setSectionDataMap,
      isPreviewMode, setPreviewMode,
      logoFontId, setLogoFontId, getLogoFont,
      logoConfig, setLogoConfig,
      isMobilePreview, setIsMobilePreview,
      discardChanges,
      discardVersion,
      reSyncFromStorage,
    }}>
      {children}
    </AdminContext.Provider>
  );
}

/** Wraps children in an admin context that reports isAdmin=false.
 *  Used by SectionVisibilityWrapper in preview mode so that all
 *  descendant section components render as plain visitor content. */
export function AdminPreviewProvider({ children }: { children: ReactNode }) {
  const parent = useContext(AdminContext);
  const previewValue = { ...parent, isAdmin: false, isEditing: false };
  return (
    <AdminContext.Provider value={previewValue}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  return useContext(AdminContext);
}